import React, { cloneElement, useEffect, useState } from 'react';
import { styled, Menu } from '@mui/material';

import { useViewport } from 'contexts/viewport';
import Avatar from 'shell/components/Avatar';
import Box from 'shell/components/Box';
import Divider from 'shell/components/Divider';
import Icon from 'shell/components/Icon';
import MenuItem from 'shell/components/MenuItem';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { useTour } from 'contexts/tour';

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        border: 'none',
        padding: 0,
        background: 'transparent',
        textAlign: 'left',
        cursor: 'pointer',
        '&:focus-visible': {
            borderRadius: '6px',
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
        },
    };
});

const CustomIcon = styled(Icon)(() => {
    return {
        fontSize: '1rem',
    };
});

const CustomMenu = styled(Menu)(({ theme }) => {
    return {
        '& .MuiBackdrop-root': {
            background: 'transparent',
        },
        '& .MuiPaper-root': {
            boxShadow: theme.palette.ensightful.shadows[1],
        },
        '& .MuiList-root': {
            padding: theme.spacing(2),
        },
    };
});

const getIcon = (key) => {
    return {
        profile: <CustomIcon kind={'avatar'} />,
        tour: <CustomIcon kind={'rocket'} />,
        'switch-course': <CustomIcon kind={'resync'} />,
        logout: <CustomIcon kind={'logout'} />,
    }[key];
};

const renderUserItem = (user) => {
    return (
        <Stack key={'user'} direction={'row'} spacing={2} tabIndex={-1}>
            <Avatar size={'medium'} user={user} />
            <Stack justifyContent={'space-evenly'}>
                <Text variant={'p1'}>{`${user.display_name || user.first_name} ${user.last_name}`}</Text>
                <Text color={'light'} variant={'p3'}>
                    {user.email}
                </Text>
            </Stack>
        </Stack>
    );
};

export default ({ id, course, options, user, onSelect }) => {
    if (!id) throw new Error('Shell: Missing id in component Dropdown.');

    const { device } = useViewport();
    const { run, currentStep } = useTour();

    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleTourClick = () => {
        document.getElementById('header-dropdown').click();
    };

    const handleClose = (e, { key, value }) => {
        e.stopPropagation();
        setAnchorEl(null);
        if (key && value) onSelect({ key, value });
    };

    useEffect(() => {
        if (run && currentStep === 7) {
            handleTourClick();
        } else if (run && currentStep !== 8) {
            setAnchorEl(null);
        }
    }, [currentStep]);

    const open = Boolean(anchorEl);

    const RootComponent =
        device === 'tablet' ? (
            <CustomStack alignItems={'center'} component={'button'} direction={'row'} spacing={2}>
                <Avatar size={'medium'} user={user} />
                <CustomIcon kind={'dropdownArrowDown'} />
            </CustomStack>
        ) : (
            <CustomStack component={'button'} direction={'row'} spacing={2}>
                <Avatar size={'medium'} user={user} />
                <Stack sx={{ height: '2.5rem' }} justifyContent={'space-evenly'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text color={'primary'} variant={'p1'}>{`${
                            user.display_name || user.first_name
                        } ${user.last_name.charAt(0)}.`}</Text>
                        <CustomIcon kind={'dropdownArrowDown'} />
                    </Stack>
                    {course?.name && <Text variant={'p3'}>{course?.name}</Text>}
                </Stack>
            </CustomStack>
        );

    return (
        <Box className={'header-1'}>
            {cloneElement(RootComponent, {
                id,
                'aria-controls': open ? `${id}-menu` : undefined,
                'aria-expanded': open ? true : undefined,
                'aria-haspopup': true,
                onClick: handleClick,
            })}
            <CustomMenu
                id={`${id}-menu`}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                MenuListProps={{
                    'aria-labelledby': id,
                }}
                open={open}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                onClose={handleClose}
            >
                {options.map(({ key, value }, index) => {
                    if (key === 'divider') return <Divider key={`${key}-${index}`} />;

                    if (key === 'user') {
                        return (
                            <MenuItem
                                key={`${key}-${index}`}
                                sx={{ height: 'auto' }}
                                onClick={(e) => handleClose(e, { key, value })}
                            >
                                {renderUserItem(user)}
                            </MenuItem>
                        );
                    }

                    return (
                        <MenuItem key={`${key}-${index}`} id={key} onClick={(e) => handleClose(e, { key, value })}>
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                {getIcon(key)}
                                {value}
                            </Stack>
                        </MenuItem>
                    );
                })}
            </CustomMenu>
        </Box>
    );
};
